import React, { useEffect, useState } from 'react';
import { Check, ChevronRight, Copy, Share } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { t } from '../../i18n';
import { localizeError } from '../../i18n/errors';
import Modal from '../ui/Modal';
import { useFormatCurrency } from '../../utils/currencyContext';
import {
  canNativeShare,
  copyText,
  displayRejoinUrl,
  shareInvite,
  shareLink,
} from '../../utils/inviteShare';
import { joinUrlForRoom } from './InviteSheet';

const PILL =
  'flex-1 h-11 rounded-full text-[14px] font-medium inline-flex items-center justify-center gap-1.5';

/**
 * Host tools → Add or rejoin. Combines the old Add players sheet and the
 * Rejoin a player picker. Tapping a disconnected seat expands that row
 * in place with the one-time link — no second sheet. Token minting and
 * /r/:code/:token links stay the same.
 */
export function AddOrRejoinSheet({
  open,
  roomCode,
  droppedPlayers = [],
  rejoinLink,
  rejoinError,
  onPickRejoin,
  onClose,
  onBack,
}) {
  const formatCurrency = useFormatCurrency();
  const [copiedJoin, setCopiedJoin] = useState(false);
  const [copiedRejoin, setCopiedRejoin] = useState(false);
  const [addExpanded, setAddExpanded] = useState(false);
  const joinUrl = joinUrlForRoom(roomCode);
  const canShare = canNativeShare();
  const rejoinOpen = !!(rejoinLink && rejoinLink.url);

  useEffect(() => {
    if (!open) {
      setCopiedJoin(false);
      setCopiedRejoin(false);
      setAddExpanded(false);
    }
  }, [open]);

  useEffect(() => {
    if (!rejoinOpen) setAddExpanded(false);
  }, [rejoinOpen]);

  const handleCopyJoin = async () => {
    try {
      await copyText(joinUrl);
      setCopiedJoin(true);
      setTimeout(() => setCopiedJoin(false), 1500);
    } catch (err) {
      console.warn('Copy failed:', err);
    }
  };

  const handleShareJoin = async () => {
    try {
      const result = await shareInvite({ url: joinUrl });
      if (result === 'copied') {
        setCopiedJoin(true);
        setTimeout(() => setCopiedJoin(false), 1500);
      }
    } catch (err) {
      console.warn('Share failed:', err);
    }
  };

  const handleCopyRejoin = async () => {
    if (!rejoinLink?.url) return;
    try {
      await copyText(rejoinLink.url);
      setCopiedRejoin(true);
      setTimeout(() => setCopiedRejoin(false), 1500);
    } catch (err) {
      console.warn('Copy failed:', err);
    }
  };

  const handleShareRejoin = async () => {
    if (!rejoinLink?.url) return;
    try {
      const result = await shareLink({ url: rejoinLink.url });
      if (result === 'copied') {
        setCopiedRejoin(true);
        setTimeout(() => setCopiedRejoin(false), 1500);
      }
    } catch (err) {
      console.warn('Share failed:', err);
    }
  };

  const expandedId = rejoinLink?.player?.id;
  const expandedName = rejoinLink?.player?.name || t('common.player');
  const expandedStack = formatCurrency(rejoinLink?.player?.stack || 0);

  return (
    <Modal
      open={!!open && !!roomCode}
      onClose={onClose}
      onBack={onBack}
      title={t('add.title')}
      compact
      zClass="z-[90]"
      testId="add-or-rejoin"
    >
      <div
        className="add-or-rejoin"
        data-rejoin-open={rejoinOpen ? 'true' : 'false'}
        data-add-expanded={addExpanded ? 'true' : 'false'}
        data-testid="add-or-rejoin-body"
      >
        <section>
          <div className="label-eyebrow mb-1.5 px-0.5">{t('add.addPlayer')}</div>
          <button
            type="button"
            onClick={() => setAddExpanded(true)}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            className="add-or-rejoin__collapsed w-full h-10 px-3 rounded-[14px] border border-line bg-transparent flex items-center gap-3 text-left"
            data-testid="add-or-rejoin-collapsed"
          >
            <span className="flex-1 min-w-0 text-[14px] font-medium text-ink truncate">
              {t('add.roomCodeQr')}
            </span>
            <span className="text-[13px] text-mute tnum tracking-[0.12em]">{roomCode}</span>
            <ChevronRight className="w-4 h-4 text-mute flex-shrink-0" aria-hidden />
          </button>
          <div
            className="add-or-rejoin__full rounded-[14px] border border-line px-4 py-3 text-center"
            data-testid="invite-sheet"
          >
            <div className="label-eyebrow text-mute">{t('home.roomCode')}</div>
            <div
              className="mt-1.5 text-[34px] font-semibold tnum tracking-[0.16em] text-ink leading-none"
              data-testid="invite-room-code"
            >
              {roomCode}
            </div>
            {joinUrl ? (
              <div
                className="add-or-rejoin__qr mt-3 inline-block bg-white p-1.5 rounded-xl border border-line"
                data-testid="invite-qr"
              >
                <QRCodeSVG
                  value={joinUrl}
                  size={120}
                  bgColor="#ffffff"
                  fgColor="#0d0d0d"
                  level="M"
                  includeMargin={false}
                />
              </div>
            ) : null}
            <div className="mt-3 flex gap-2">
              {canShare && (
                <button
                  type="button"
                  onClick={handleShareJoin}
                  className={`${PILL} bg-ink text-cream font-semibold`}
                  data-testid="invite-share"
                >
                  <Share className="w-4 h-4" />
                  {t('common.share')}
                </button>
              )}
              <button
                type="button"
                onClick={handleCopyJoin}
                className={`${PILL} border border-line bg-transparent text-ink`}
                data-testid="invite-copy"
              >
                {copiedJoin ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copiedJoin ? t('common.copied') : t('common.copy')}
              </button>
            </div>
          </div>
        </section>

        <section className="mt-3" data-testid="rejoin-picker">
          <div className="label-eyebrow mb-1.5 px-0.5">{t('add.rejoinPlayer')}</div>
          {rejoinError && (
            <p className="text-[13px] text-accent-danger mb-2 m-0" role="alert">{localizeError(rejoinError)}</p>
          )}
          {droppedPlayers.length === 0 ? (
            <div className="rounded-[14px] border border-line px-3 py-3">
              <p className="text-[13.5px] text-mute m-0">{t('add.nobodyDropped')}</p>
            </div>
          ) : (
            <div className="rounded-[14px] border border-line overflow-hidden divide-y divide-line">
              {droppedPlayers.map((p) => {
                const expanded = expandedId === p.id && rejoinOpen;
                return (
                  <div key={p.id} className="bg-transparent">
                    <button
                      type="button"
                      onClick={() => onPickRejoin?.(p)}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                      className="w-full min-h-[44px] px-3 bg-transparent border-0 flex items-center justify-between gap-3 text-left"
                      data-testid={`pick-player-${p.name}`}
                    >
                      <span className="text-[14px] font-medium text-ink truncate">{p.name}</span>
                      <span className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-[13px] text-mute tnum whitespace-nowrap">
                          {formatCurrency(p.stack || 0)}
                        </span>
                        <ChevronRight className="w-4 h-4 text-mute" aria-hidden />
                      </span>
                    </button>
                    {expanded && (
                      <div className="px-3 pb-3" data-testid="rejoin-link-modal">
                        <p className="text-[13.5px] text-mute m-0 mb-2">
                          {t('add.sendTo', { name: expandedName, amount: expandedStack })}
                        </p>
                        <div
                          className="w-full min-h-10 px-3 py-2 rounded-[12px] border border-line bg-transparent text-[13px] text-ink break-all"
                          data-testid="rejoin-link-url"
                        >
                          {displayRejoinUrl(rejoinLink.url)}
                        </div>
                        <div className="mt-2 flex gap-2">
                          <button
                            type="button"
                            onClick={handleShareRejoin}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            className={`${PILL} bg-ink text-cream font-semibold`}
                            data-testid="rejoin-share"
                          >
                            {t('common.share')}
                          </button>
                          <button
                            type="button"
                            onClick={handleCopyRejoin}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            className={`${PILL} border border-line bg-transparent text-ink`}
                            data-testid="rejoin-copy"
                          >
                            {copiedRejoin ? t('common.copied') : t('common.copy')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="add-or-rejoin__helper text-[12.5px] text-mute m-0 mt-2 px-0.5">
            {t('add.whoDropped')}
          </p>
        </section>
      </div>
    </Modal>
  );
}
